
import { useState } from 'react';
import { Link, useLocation, useNavigate, useRouteError } from 'react-router';
import { wordsOfPath } from '../auth/return-to';
import { useI18n } from '../i18n';
import type { MessageKey } from '@smbsuite/i18n';
import { EmptyState, Icon, TextField, type IconName } from '../ui';
import { useDestinations } from './navigation';
import { STATUS_PAGE_URL } from './status-page';

export function ErrorPage() {
  const error = useRouteError();
  const { t } = useI18n();
  console.error(error);
  return (
    <main className="center-page">
      <div className="center-card">
        <EmptyState icon={<Icon name="warning" size={40} />} title={t('error.title')}>
          <p>{t('error.hint')}</p>
          {/* Down for everybody, or only here? (TF-133) */}
          <p className="muted">
            {t('status.question')}{' '}
            <a href={STATUS_PAGE_URL} target="_blank" rel="noopener">
              {t('status.page')}
            </a>
          </p>
          <div className="btn-row justify-center">
            <button type="button" className="btn" onClick={() => location.reload()}>
              {t('common.retry')}
            </button>
            <a className="btn btn-primary" href="/">
              {t('error.home')}
            </a>
          </div>
        </EmptyState>
      </div>
    </main>
  );
}

/** The places a lost person is shown, in this order, from the navigation's own list (TF-235). */
const PLACES: { key: string; description: MessageKey; label?: MessageKey }[] = [
  { key: 'home', description: 'notFound.dashboard' },
  { key: 'deals', description: 'notFound.deals' },
  { key: 'accounts', description: 'notFound.accounts' },
  { key: 'contacts', description: 'notFound.contacts' },
  { key: 'tasks', description: 'notFound.tasks', label: 'notFound.tasksLabel' },
  { key: 'files', description: 'notFound.files' },
  { key: 'search', description: 'notFound.search' },
  { key: 'shared', description: 'notFound.shared' },
  { key: 'settings', description: 'notFound.settings' },
];

/**
 * An address the app has no page for, signed in (TF-235): the same address, and where things are - the
 * places this person's role can open, as the navigation lists them, a search with the address's words,
 * and the start page.
 */
export function NotFoundPage() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const destinations = useDestinations();
  const [words, setWords] = useState(() => wordsOfPath(pathname));
  const dashboard = destinations.find((destination) => destination.key === 'home');
  const searchPath = dashboard ? `${dashboard.to}/search` : null;
  const found = new Map<string, { to: string; icon: IconName; label: string }>(destinations.map((destination) => [destination.key, destination]));
  if (searchPath) found.set('search', { to: searchPath, icon: 'search', label: t('search.title') });
  // A member has the company's pages; Shared is where a guest's things are.
  if (dashboard) found.delete('shared');
  const places = PLACES.flatMap((place) => {
    const destination = found.get(place.key);
    return destination ? [{ ...place, ...destination, label: place.label ? t(place.label) : destination.label }] : [];
  });
  return (
    <div className="not-found">
      <div className="page-head">
        <h1>{t('error.notFound')}</h1>
      </div>
      <p className="muted">{t('notFound.lead')}</p>
      {searchPath && (
        <form
          className="not-found-search"
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            const query = words.trim();
            navigate(query ? `${searchPath}?q=${encodeURIComponent(query)}` : searchPath);
          }}
        >
          <TextField id="not-found-search" name="q" type="search" label={t('search.title')} value={words} onChange={setWords} maxLength={100} />
          <button type="submit" className="btn btn-primary">
            <Icon name="search" size={18} />
            {t('notFound.searchButton')}
          </button>
        </form>
      )}
      <ul className="place-grid" aria-label={t('notFound.places')}>
        {places.map((place) => (
          <li key={place.key} className="card card-link place-card">
            <Icon name={place.icon} />
            <div>
              <h2>
                <Link className="card-link-target" to={place.to}>
                  {place.label}
                </Link>
              </h2>
              <p className="muted">{t(place.description)}</p>
            </div>
          </li>
        ))}
      </ul>
      <Link className="btn" to="/">
        {t('error.home')}
      </Link>
    </div>
  );
}
